import React, { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import useFetch from "Hooks/useFetch";
import IsLoading from "Components/RequestHandler/IsLoading";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import { useTranslation } from "react-i18next";
import "../index.css";
import InstructorCard from "../Common/InstructorCard";
import { t } from "i18next";
import VideoStatusIndicator from "../Common/VideoStatusIndicator";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import LessonCompletedBadge from "Components/LessonCompletedBadge";
import { videoThumb, cardThumb } from "Utilities/courseImages";

const WatchSeries = () => {
  const { i18n } = useTranslation();
  const { slug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [courseData, setCourseData] = useState(null);
  const { fetchData } = useFetch(`courses/${slug}`);
  const { token, selectedUser } = useContext(LoginAuthContext);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [completedVideos, setCompletedVideos] = useState({});
  const [videoProgress, setVideoProgress] = useState({}); // Map of videoId -> { timestamp, timeSlap }
  const [loadingVideoId, setLoadingVideoId] = useState(null);
  const [autoPlayNext, setAutoPlayNext] = useState(false);

  const getCourse = async () => {
    try {
      const profileParam = selectedUser?.id ? `?profile_id=${selectedUser.id}` : "";
      const response = await fetchData(`courses/${slug}${profileParam}`, token);
      const data = response?.data;

      setCourseData(data);

      // Resume at the first NOT-completed episode (better UX than always
      // restarting at episode 1). Falls back to the first episode when every
      // episode is already done.
      const doneSet = new Set(data?.completed_videos || []);
      const episodeIds = (data?.series || [])
        .map((item) => item?.series_video_id?.videoId)
        .filter(Boolean);
      const firstNotDone = episodeIds.find((id) => !doneSet.has(id));
      setSelectedVideo(firstNotDone || episodeIds[0] || data?.trailer?.videoId);

      // Set completed videos from API response
      const completed = {};
      if (data?.completed_videos) {
        data.completed_videos.forEach((videoId) => {
          completed[videoId] = true;
        });
      }

      // Also check is_done flag on each series item (set by backend)
      if (data?.series) {
        data.series.forEach((item) => {
          if (item?.is_done) {
            const videoId = item?.series_video_id?.videoId;
            if (videoId) {
              completed[videoId] = true;
            }
          }
        });
      }

      setCompletedVideos(completed);

      // Set video progress from API response
      if (data?.video_progress) {
        setVideoProgress(data.video_progress);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCourse();
  }, [slug, token, selectedUser?.id]);

  // Add effect to scroll to top when selected video changes
  useEffect(() => {
    if (selectedVideo) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedVideo]);

  // Handle video progress updates - wrapped in useCallback to prevent infinite loops
  const handleProgressUpdate = useCallback((videoId, percentComplete) => {
    if (percentComplete >= 75) {
      setLoadingVideoId((current) => {
        // Only set if not already loading this video
        if (current !== videoId) return videoId;
        return current;
      });
    }
  }, []);

  // Handle when a video is marked as complete - wrapped in useCallback
  const handleVideoCompleted = useCallback((videoId) => {
    setCompletedVideos((prev) => {
      // Only update if not already marked as completed
      if (prev[videoId]) return prev;
      return { ...prev, [videoId]: true };
    });
    setLoadingVideoId(null);
  }, []);

  // Auto-advance to the next episode when the current one ends
  const handleVideoEnded = useCallback(
    (endedVideoId) => {
      const series = courseData?.series || [];
      const currentIndex = series.findIndex(
        (item) => item?.series_video_id?.videoId === endedVideoId
      );
      if (currentIndex < 0) return;
      const next = series[currentIndex + 1];
      const nextId = next?.series_video_id?.videoId;
      if (nextId) {
        setAutoPlayNext(true);
        setSelectedVideo(nextId);
      }
    },
    [courseData?.series]
  );

  // Reset autoPlayNext when the user manually selects a video
  const handleManualSelect = useCallback((videoId) => {
    setAutoPlayNext(false);
    setSelectedVideo(videoId);
  }, []);

  // The episode that comes after the one currently playing — drives the
  // "Up Next" countdown overlay; null when this is the last episode.
  const nextItem = useMemo(() => {
    const series = courseData?.series || [];
    const currentIndex = series.findIndex(
      (item) => item?.series_video_id?.videoId === selectedVideo
    );
    const next = currentIndex >= 0 ? series[currentIndex + 1] : null;
    if (!next?.series_video_id?.videoId) return null;

    return {
      title: next.title,
      thumbnail: videoThumb(next.series_video_id) || cardThumb(courseData),
    };
  }, [courseData, selectedVideo]);

  // Memoize video progress for selected video to prevent new object references
  const selectedVideoProgress = useMemo(() => {
    if (!selectedVideo) return null;

    // First check video_progress from API
    if (videoProgress[selectedVideo]) {
      return videoProgress[selectedVideo];
    }

    // Then check series item video_progress
    const seriesItem = courseData?.series?.find(
      (item) => item?.series_video_id?.videoId === selectedVideo
    );
    return seriesItem?.video_progress || null;
  }, [selectedVideo, videoProgress, courseData?.series]);

  // Memoize if selected video is done
  const isSelectedVideoDone = useMemo(() => {
    if (!selectedVideo) return false;
    return completedVideos[selectedVideo] || false;
  }, [selectedVideo, completedVideos]);

  if (isLoading) return <IsLoading />;
  if (courseData) {
    return (
      <main className="mb-secondary lg:mb-primary mt-2">
        <div className="w-full flex flex-col lg:flex-row">
          <div className="lg:w-[70%] border-b border-primary lg:border-none">
            <VideoPlayer
              videoData={courseData}
              videoId={selectedVideo}
              courseId={courseData?._id}
              courseSlug={slug}
              videoProgress={selectedVideoProgress}
              initialIsDone={isSelectedVideoDone}
              onProgressUpdate={handleProgressUpdate}
              onVideoCompleted={handleVideoCompleted}
              onEnded={handleVideoEnded}
              nextItem={nextItem}
              autoPlayOnLoad={autoPlayNext}
            />
          </div>

          <div className="flex flex-col lg:w-[30%] px-4 lg:px-6 my-6 lg:mt-10">
            {/* instructor profile section */}
            <InstructorCard data={courseData?.instructor} />

            {/* lesson counts */}
            <div className="bg-grey/30 rounded-lg px-4 py-3 mt-6">
              <p
                className={`text-white flex items-center gap-2 ${
                  courseData?.is_course_completed ? "justify-between" : ""
                }`}
              >
                <span className="font-medium">
                  {courseData?.series?.length} {t("courses.lessons")}
                </span>

                {courseData?.is_course_completed && (
                  <LessonCompletedBadge variant="text" size="medium" />
                )}
              </p>
            </div>

            {/* lessons */}
            <div className="mt-6 max-h-[50vh] flex flex-col gap-y-3 overflow-y-auto video-series-scrollbar pr-2">
              {courseData?.series?.map((item, index) => (
                <button
                  onClick={() => {
                    handleManualSelect(item?.series_video_id?.videoId);
                  }}
                  className={`flex items-center text-start gap-x-3 w-full p-3 rounded-lg transition-colors duration-200 ${
                    selectedVideo === item?.series_video_id?.videoId
                      ? "bg-primary text-white"
                      : "hover:bg-lightGrey bg-grey"
                  }`}
                  key={index}
                >
                  <div className="relative shrink-0">
                    <img
                      src={videoThumb(item?.series_video_id) || cardThumb(courseData)}
                      alt={item.title}
                      className={`w-20 h-14 object-cover rounded ${
                        selectedVideo === item?.series_video_id?.videoId
                          ? "ring-2 ring-primary"
                          : ""
                      }`}
                    />
                    <div
                      className={`absolute inset-0 flex items-center justify-center rounded `}
                    >
                      <VideoStatusIndicator
                        isDone={completedVideos[item?.series_video_id?.videoId] || item?.is_done}
                        isSelected={
                          selectedVideo === item?.series_video_id?.videoId
                        }
                        isLoading={
                          loadingVideoId === item?.series_video_id?.videoId
                        }
                      />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-400 mb-1">
                      {t("courses.lesson")}{" "}
                      {i18n.language === "ar"
                        ? (index + 1).toLocaleString("ar-EG")
                        : index + 1}
                    </span>
                    <h3 className="text-white text-sm font-medium line-clamp-2">
                      {item.title}
                    </h3>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }
};

export default WatchSeries;
